// ============================================================================
// ProjectUserManage.js
// -----------------------------------------------------------------------------
// Modale de gestion des membres d’un projet
// Affiche, ajoute et modifie les rôles des utilisateurs
// ============================================================================
import React, { useState, useEffect } from "react";
import theme from "../../../styles/theme";
import Button from "../../reusable/Forms/Button";
import CardUser from "../../reusable/Card/CardUser";
import { PROJECT_ROLES } from "../../../base/constants";
import { API_ROUTES } from "../../../config/api";
import{apiFetch} from "../../../config/apiFetch";
import ModalTemplate from "../../templates/ModalTemplate";
import TextFilter from "../../reusable/Forms/TextFilter"; // ✅ ajout de l’import correct

// ============================================================================
const ProjectUserManage = ({ isOpen, onClose, projectId, onRefresh }) => {
  // --------------------------------------------------------------------------
  // États principaux
  // --------------------------------------------------------------------------
  const [projectUsers, setProjectUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedNewUsers, setSelectedNewUsers] = useState([]);
  const [modifiedRoles, setModifiedRoles] = useState({});

  // --------------------------------------------------------------------------
  // Chargement des données
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();

    const loadData = async () => {
      try {
        const resMembers = await apiFetch(API_ROUTES.projectUsers.byProject(projectId), { signal: controller.signal });
        if (!resMembers.ok) throw new Error("Erreur chargement membres projet");
        const members = await resMembers.json();
        setProjectUsers(members);

        const currentUser = JSON.parse(localStorage.getItem("corelia_user") || "{}");

        const resAvailable = await apiFetch(
          `${API_ROUTES.accounts.allUsers}?accountId=${currentUser.accountId}`,
          { signal: controller.signal }
        );

        if (!resAvailable.ok) throw new Error("Erreur chargement utilisateurs compte");
        const allUsers = await resAvailable.json();

        const currentIds = new Set(members.map((m) => m.userId));
        setAvailableUsers(allUsers.filter((u) => !currentIds.has(u.id)));
      } catch (e) {
        console.error("Erreur chargement membres :", e);
      }
    };

    loadData();
    return () => controller.abort();
  }, [isOpen, projectId]);

  // --------------------------------------------------------------------------
  // Gestion des rôles
  // --------------------------------------------------------------------------
  const handleRoleChange = (userId, roleKey) =>
    setModifiedRoles((prev) => ({ ...prev, [userId]: roleKey }));

  // --------------------------------------------------------------------------
  // Ajout / sélection utilisateur
  // --------------------------------------------------------------------------
  const handleSelectUser = (user) => {
    setSelectedNewUsers((prev) =>
      prev.find((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user]
    );
  };

  // --------------------------------------------------------------------------
  // Sauvegarde
  // --------------------------------------------------------------------------
  const handleSave = async () => {
    try {
      if (selectedNewUsers.length) {
        const payload = selectedNewUsers.map((u) => ({
          userId: u.id,
          projectId,
          roleInProject: "Collaborator",
          invitationStatus: "Accepted",
        }));

        const resAdd = await apiFetch(API_ROUTES.projectUsers.add, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!resAdd.ok) throw new Error(`HTTP ${resAdd.status}`);
      }

      if (Object.keys(modifiedRoles).length) {
        const updates = Object.entries(modifiedRoles).map(([userId, roleKey]) => ({
          projectId,
          userId: parseInt(userId, 10),
          roleInProject: PROJECT_ROLES[roleKey],
        }));

        const resUpdate = await apiFetch(API_ROUTES.projectUsers.updateRoles, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        });
        if (!resUpdate.ok) throw new Error(`HTTP ${resUpdate.status}`);
      }

      if (typeof onRefresh === "function") await onRefresh();
      onClose();
    } catch (err) {
      console.error("Erreur sauvegarde :", err);
      alert("Erreur lors de l’enregistrement des modifications");
    }
  };

  // --------------------------------------------------------------------------
  // Ajout direct d’un utilisateur
  // --------------------------------------------------------------------------
  const handleAddUserToProject = async (user) => {
    try {
      const payload = [{
        userId: user.id,
        projectId,
        roleInProject: "Collaborator",
        invitationStatus: "Accepted",
      }];

      const res = await apiFetch(API_ROUTES.projectUsers.add, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setAvailableUsers((prev) => prev.filter((u) => u.id !== user.id));
      setProjectUsers((prev) => [
        ...prev,
        {
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          roleInProject: "Viewer",
          invitationStatus: "Accepted",
        },
      ]);
    } catch (err) {
      console.error("Erreur ajout utilisateur :", err);
      alert("Impossible d’ajouter cet utilisateur au projet.");
    }
  };

  // --------------------------------------------------------------------------
  // Rendu
  // --------------------------------------------------------------------------
  if (!isOpen) return null;

  const divider = (
    <hr
      style={{
        border: "none",
        borderTop: `1px solid ${theme.colors.borders.default}`,
        margin: `${theme.spacing.md}px 0`,
      }}
    />
  );

  return (
    <ModalTemplate
      isOpen={isOpen}
      title="Gestion des membres"
      onClose={onClose}
      onSubmit={handleSave}
      width="650px"
    >
      <p style={{ color: theme.colors.text.secondary, marginBottom: theme.spacing.xs, fontSize: theme.fontSizes.sm , marginBottom: "12px"}}>
        Ajoutez ou retirez des membres et gérez les permissions.
      </p>

      <div style={{ display: "flex", alignItems: "center", marginBottom: theme.spacing.md, gap: 8, fontSize: theme.fontSizes.sm, fontWeight: theme.fontWeights.light }}>
        <img src="/assets/icones/users.svg" alt="users" style={{ width: 18, height: 18 }} />
        <strong>Membres du projet</strong>
        <span style={{ color: theme.colors.text.secondary }}>({projectUsers.length})</span>
      </div>

      <div style={{ marginBottom: theme.spacing.lg}}>
        {projectUsers.map((u) => (
          <CardUser
            key={u.userId}
            fullName={`${u.firstName} ${u.lastName}`}
            email={u.email}
            role={u.roleInProject}
            userId={u.userId}
            projectId={projectId}
            onRoleChange={handleRoleChange}
            onUserRemoved={(userId) => {
              setProjectUsers((prev) => prev.filter((m) => m.userId !== userId));
              const removed = projectUsers.find((m) => m.userId === userId);
              if (removed)
                setAvailableUsers((prev) => [
                  ...prev,
                  {
                    id: removed.userId,
                    firstName: removed.firstName,
                    lastName: removed.lastName,
                    email: removed.email,
                  },
                ]);
            }}
          />
        ))}
      </div>

      {divider}

      <div style={{ marginBottom: theme.spacing.sm, fontSize: theme.fontSizes.sm }}>
        <strong style={{ color: theme.colors.text.corelia }}>+ Ajouter des membres</strong>
      </div>

      {/* ✅ Remplacement de l'ancien <Input> par TextFilter */}
      <TextFilter
        label=""
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher un membre dans le compte..."
        disabled={false}
        style={{
          marginBottom: theme.spacing.sm,
          width: "100%",
        }}
        inputStyle={{
          fontSize: theme.fontSizes.sm,
        }}
      />

      <div style={{ marginTop: theme.spacing.md, marginBottom: theme.spacing.lg, fontSize: theme.fontSizes.sm, fontWeight: theme.fontWeights.light }}>
        {availableUsers
          .filter(
            (u) =>
              (u.firstName + " " + u.lastName)
                .toLowerCase()
                .includes(search.toLowerCase()) ||
              u.email.toLowerCase().includes(search.toLowerCase())
          )
          .map((u) => (
            <div
              key={u.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 8px",
                borderRadius: theme.borders.radius.md,
                marginBottom: 4,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.sm, overflow: "hidden" }}>
                <img src="/assets/icones/user.svg" alt="user" style={{ width: 16, height: 16, flexShrink: 0 }} />
                <div style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                  <span style={{ color: theme.colors.text.default }}>
                    {u.firstName} {u.lastName}
                  </span>{" "}
                  <span style={{ fontSize: "8px", color: theme.colors.text.default }}>■</span>{" "}
                  <span style={{ color: theme.colors.text.body, fontSize: theme.fontSizes.xs }}>{u.email}</span>
                </div>
              </div>
              <Button
                variant="white"
                size="small"
                onClick={() => handleAddUserToProject(u)}
                style={{
                  minWidth: 24,
                  height: 24,
                  padding: 0,
                  fontSize: theme.fontSizes.sm,
                  fontWeight: theme.fontWeights.bold,
                  borderRadius: theme.borders.radius.sm,
                  backgroundColor: theme.colors.background.app,
                }}
              >
                +
              </Button>
            </div>
          ))}
      </div>
    </ModalTemplate>
  );
};

export default ProjectUserManage;

